import { Badge, Button, Field, Drawer, DrawerBody, DrawerHeader, DrawerHeaderTitle, Text, Spinner, ProgressBar } from "@fluentui/react-components";
import { AzureMap, AzureMapsProvider, AzureMapHtmlMarker, IAzureMapOptions } from 'react-azure-maps';
import { AuthenticationType, ControlOptions } from 'azure-maps-control';
import * as atlas from 'azure-maps-control';
import "./styles.scss";
import InfiniteDropdown from "#root/components/InfiniteDropdown";
import LoadingWrapper from '#root/components/LoadingWrapper';
import { useState, useCallback, useEffect, useRef } from "react";
import { useInfiniteIncidents, useIncidentById } from "#root/queries/incidents";
import { useInfiniteDropdown } from "#root/hooks/useInfiniteDropdown";
import 'azure-maps-control/dist/atlas.min.css';
import { Dismiss16Regular, PrintFilled } from "@fluentui/react-icons";
import { MapLocation, useMapLocations, useMapStatistics, useMapVictims } from "#root/queries/map";
import { domToPng } from 'modern-screenshot';
import jsPDF from 'jspdf';
import LazyImage from "../LazyImage";
import { formatToLocalTime } from '#root/services/utils';

const MAP_STYLES = {
    GRAYSCALE: 'grayscale_light',
    ROAD_SHADED: 'road_shaded_relief',
    SATELLITE: 'satellite_road_labels'
};

declare global {
    interface Window {
        atlas: any;
    }
}

export interface BaseMapVictimsProps {
    initialIncidentId?: string;
    onIncidentChange?: (id: string | null) => void;
    enablePdfButton?: boolean;
    customControls?: { controlName: string; options: ControlOptions }[];
    customMapOptions?: Partial<IAzureMapOptions>;
    customHeaderContent?: React.ReactNode;
    mode?: 'viewer' | 'admin';
}

const BaseMapVictims: React.FC<BaseMapVictimsProps> = ({
    initialIncidentId = '',
    onIncidentChange,
    enablePdfButton = false,
    customControls,
    customMapOptions = {},
    customHeaderContent,
    mode = 'viewer'
}) => {
    const [selectedLocation, setSelectedLocation] = useState<MapLocation | null>(null);
    const [isZooming, setIsZooming] = useState(false);
    const [mapReady, setMapReady] = useState(false);
    const [popups, setPopups] = useState<Record<string, atlas.Popup>>({});
    const [hoveredLocationId, setHoveredLocationId] = useState<string | null>(null);
    const [currentMapStyle, setCurrentMapStyle] = useState<string>(MAP_STYLES.GRAYSCALE);
    const printRef = useRef<HTMLDivElement>(null);
    const mapContainer = useRef<HTMLDivElement>(null);
    const isAdmin = mode === 'admin';
    const [isPdfLoading, setIsPdfLoading] = useState(false);
    const [pdfProgress, setPdfProgress] = useState({
        current: 0,
        total: 1
    });
    const mapRef = useRef<atlas.Map | null>(null);
    const loadingTimeoutRef = useRef<NodeJS.Timeout>();
    const [showLoading, setShowLoading] = useState(false);

    const mapControls = customControls || [
        { controlName: 'ZoomControl', options: { position: 'bottom-left' } as ControlOptions },
        {
            controlName: 'StyleControl',
            controlOptions: { mapStyles: [MAP_STYLES.GRAYSCALE, MAP_STYLES.ROAD_SHADED, MAP_STYLES.SATELLITE] },
            options: { position: 'top-right' } as ControlOptions,
        },
        { controlName: 'FullscreenControl', options: { position: 'top-right' } as ControlOptions },
    ];

    const defaultMapOptions: IAzureMapOptions = {
        authOptions: {
            authType: AuthenticationType.subscriptionKey,
            subscriptionKey: process.env.REACT_APP_AZURE_MAPS
        },
        renderWorldCopies: false,
        preserveDrawingBuffer: true,
        tileBuffer: 2,  // Add buffer to handle geometries near tile edges
        controls: mapControls,
        showLabels: true,
        showLogo: false,
        style: MAP_STYLES.GRAYSCALE,
        enableAccessibility: true,
        showFeedbackLink: false,
        maxZoom: 18
    };

    const mapOptions = { ...defaultMapOptions, ...customMapOptions };

    const {
        selectedId: selectedIncidentId,
        selectedData: selectedIncidentData,
        handleChange: handleIncidentChange,
        setFilter: setIncidentFilter,
        queryResult: incidentQueryResult
    } = useInfiniteDropdown({
        initialId: initialIncidentId || undefined,
        onChange: onIncidentChange,
        useInfiniteQuery: useInfiniteIncidents,
        useItemById: useIncidentById,
        queryParameters: { isActive: true }
    });

    // Fetch map locations and statistics
    const { data: locationsData, isFetching: locationsIsFetching } = useMapLocations({ incidentId: selectedIncidentId || undefined });
    const locations = locationsData?.items ?? [];

    const { data: statisticsData } = useMapStatistics({ incidentId: selectedIncidentId || undefined });
    const statistics = statisticsData?.item;

    // Fetch victims for selected location (admin mode only)
    const { data: victimsData } = useMapVictims(
        {
            locationId: selectedLocation?.location.id ?? '',
            incidentId: selectedIncidentId || undefined,
            isIncident: selectedLocation?.isIncident ?? false
        },
        { enabled: !!selectedLocation }
    );

    const handleMarkerClick = useCallback((location: MapLocation) => {
        if (isAdmin) {
            setSelectedLocation(location);
        }
    }, [isAdmin]);

    // Debounce loading state changes
    const isMapLoading = locationsIsFetching || isZooming;

    useEffect(() => {
        // Only show loading if we're actually fetching new locations
        if (locationsIsFetching && !locationsData) {
            setShowLoading(true);
        } else {
            loadingTimeoutRef.current = setTimeout(() => {
                setShowLoading(false);
            }, 100);
        }

        return () => {
            if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
            }
        };
    }, [isMapLoading, locations, locationsData, locationsIsFetching]);

    // Manage popups
    useEffect(() => {
        Object.values(popups).forEach(popup => popup.close());

        const newPopups = locations.reduce((acc, item) => {
            if (item.location?.id) {
                acc[item.location.id] = new atlas.Popup({
                    pixelOffset: [0, -40],
                    closeButton: false,
                    showPointer: false,
                    content: `
                        <div class="popup-content">
                            <div><span class="popup-content__title">${item.location.name}</span></div>
                            <div>
                                <span class="popup-content__label">Victims: </span>
                                <span class="popup-content__value">${item.statistics.totalVictims}</span>
                            </div>
                        </div>
                    `
                });
            }
            return acc;
        }, {} as Record<string, atlas.Popup>);

        setPopups(newPopups);
        return () => Object.values(newPopups).forEach(popup => popup.close());
    }, [locations, selectedIncidentId]);

    // Reset map ready state when component unmounts
    useEffect(() => {
        const cleanup = () => {
            // Only cleanup if unmounting, not just updating
            setTimeout(() => {
                if (!document.querySelector('.map-container')) {
                    setMapReady(false);
                    mapRef.current = null;
                }
            }, 0);
        };
        return cleanup;
    }, []);

    const updateMapBounds = useCallback(() => {
        if (!mapRef.current || !locations.length) {
            return;
        }

        const points = locations
            .filter(item => item.location?.id && item.coordinates)
            .map(item => [item.coordinates.longitude, item.coordinates.latitude]);

        if (points.length > 0) {
            try {
                const west = Math.min(...points.map(p => p[0]));
                const south = Math.min(...points.map(p => p[1]));
                const east = Math.max(...points.map(p => p[0]));
                const north = Math.max(...points.map(p => p[1]));

                const bounds = new atlas.data.BoundingBox([west, south], [east, north]);

                mapRef.current.setCamera({
                    bounds,
                    padding: {
                        top: 100,
                        bottom: 20,
                        left: 100,
                        right: 100
                    },
                    duration: 500
                });
            } catch (error) {
                console.error('Error setting map bounds:', error);
            }
        } else {
            console.log('No valid points found after filtering');
        }
    }, [locations]);

    // Handle map initialization and zoom
    useEffect(() => {
        if (!mapReady || !locations.length) {
            setIsZooming(false);
            return;
        }

        const hasLocationsWithCoordinates = locations.some(item => item.coordinates);
        if (hasLocationsWithCoordinates) {
            setIsZooming(true);
            updateMapBounds();

            // Reset zoom state after animation completes
            setTimeout(() => {
                setIsZooming(false);
            }, 600);
        }
    }, [mapReady, locations, selectedIncidentId, updateMapBounds]);

    const handlePrintToPDF = async () => {
        try {
            setIsPdfLoading(true);
            const printArea = printRef.current;
            if (!printArea) return;

            printArea.classList.add('printing');

            // Force a map redraw to ensure all elements are rendered
            window.dispatchEvent(new Event('resize'));

            // Wait for any animations or rendering to complete
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Take screenshot using modern-screenshot
            const screenshot = await domToPng(printArea, {
                scale: 2, // DPI = 96 * 2
                backgroundColor: '#fff',
                quality: 1,
                features: {
                    // Enable scrolled content capture
                    restoreScrollPosition: true,
                    // Fix svg+xml image decode for Safari
                    fixSvgXmlDecode: true
                },
                // Increase timeout for large maps
                timeout: 60000,
                // Handle progress
                progress: (current, total) => {
                    setPdfProgress({ current: current / total, total: 1 });
                },
                // Custom fetch options
                fetch: {
                    bypassingCache: true,
                    requestInit: {
                        cache: 'no-store'
                    }
                },
                // Ensure proper image rendering
                drawImageInterval: 200
            });

            printArea.classList.remove('printing');

            // Create PDF with the screenshot
            const pdf = new jsPDF({
                orientation: 'landscape',
                unit: 'px',
                format: [printArea.offsetWidth, printArea.offsetHeight],
                hotfixes: ['px_scaling']
            });

            // Add the screenshot to PDF
            pdf.addImage(
                screenshot,
                'PNG',
                0,
                0,
                printArea.offsetWidth,
                printArea.offsetHeight
            );

            // Generate filename with date and time
            const date = new Date();
            const formattedDate = formatToLocalTime(date, 'date').replace(/\//g, '-');
            const formattedTime = formatToLocalTime(date, 'time').replace(/:/g, '-');
            pdf.save(`map-export-${formattedDate}-${formattedTime}.pdf`);
        } catch (error) {
            console.error('Error generating PDF:', error);
            printRef.current?.classList.remove('printing');
        } finally {
            setIsPdfLoading(false);
            setPdfProgress({ current: 0, total: 1 });
        }
    };

    // Update selected location when incident changes to get new statistics
    useEffect(() => {
        if (selectedLocation) {
            const updatedLocation = locations.find(loc => loc.location.id === selectedLocation.location.id);
            if (updatedLocation) {
                setSelectedLocation(updatedLocation);
            } else {
                setSelectedLocation(null);
            }
        }
    }, [selectedIncidentId, locations]);

    // Filter out simple locations that match incident addresses
    const filteredLocations = locations.reduce((acc, location) => {
        if (location.isIncident) {
            acc.push(location);
            return acc;
        }

        // Check if this simple location's address matches any incident location
        const hasMatchingIncident = locations.some(incident =>
            incident.isIncident &&
            incident.location?.address?.toLowerCase() === location.location?.address?.toLowerCase()
        );

        if (!hasMatchingIncident) {
            acc.push(location);
        }

        return acc;
    }, [] as MapLocation[]);

    // Sort locations to keep incidents on top
    const sortedLocations = [...filteredLocations].sort((a, b) => {
        if (a.isIncident && !b.isIncident) return 1;
        if (!a.isIncident && b.isIncident) return -1;
        return 0;
    });

    return (
        <>
            <div className="base-map" ref={printRef}>
                <div className="base-map__content">
                    <div className="base-map__header">
                        {customHeaderContent}
                        <div className={`base-map__controls ${enablePdfButton ? 'has-pdf' : ''}`}>
                            <Field label="Incident" className="base-map__controls--incident">
                                <InfiniteDropdown
                                    value={selectedIncidentId || ''}
                                    onChange={(_, data) => handleIncidentChange(data.value)}
                                    onFilterChange={setIncidentFilter}
                                    queryResult={incidentQueryResult}
                                    itemToOption={(incident) => ({
                                        value: incident.id ?? '',
                                        text: incident.name ?? ''
                                    })}
                                    placeholder="All Incidents"
                                    selectedItem={selectedIncidentData}
                                    clearable
                                />
                            </Field>
                            {enablePdfButton && (
                                <div className="base-map__controls--pdf-button">
                                    <Button appearance="primary" icon={<PrintFilled />} onClick={handlePrintToPDF}>Print to PDF</Button>
                                </div>
                            )}
                            <div className="base-map__controls--badges">
                                <Badge color="success" size="extra-large">Total Victims: {statistics?.totalVictims ?? '-'}</Badge>
                                <Badge appearance="tint" color="success" size="extra-large">Total Entered by Public: {statistics?.totalPublic ?? '-'}</Badge>
                                <Badge appearance="tint" color="success" size="extra-large">Total Matched: {statistics?.totalMatched ?? '-'}</Badge>
                                <Badge appearance="tint" color="success" size="extra-large">Total Notifications Completed: {statistics?.totalConnected ?? '-'}</Badge>
                            </div>
                        </div>
                    </div>

                    <div className="map-container" ref={mapContainer}>
                        <AzureMapsProvider>
                            <AzureMap
                                options={mapOptions}
                                controls={mapControls}
                                events={{
                                    ready: (e: atlas.Map) => {
                                        const mapInstance = (e as any).map || e;
                                        mapRef.current = mapInstance;
                                        setMapReady(true);

                                        // Listen for style changes
                                        mapInstance.events.add('styledata', () => {
                                            const style = mapInstance.getStyle().style;
                                            setCurrentMapStyle(style);
                                        });
                                    }
                                }}
                            >
                                {sortedLocations
                                    .filter(item => item.location?.id && item.coordinates)
                                    .map(item => {
                                        const isHovered = hoveredLocationId === item.location.id;
                                        const markerIcon = item.isIncident ? "/icons/incident-marker.svg" : "/icons/marker.svg";
                                        return (
                                            <AzureMapHtmlMarker
                                                key={item.location.id}
                                                isPopupVisible={isHovered}
                                                markerContent={
                                                    <div
                                                        className=
                                                        {
                                                            `map-marker 
                                                                ${isHovered ? 'map-marker--hovered' : ''} 
                                                                ${!item.isIncident ? 'map-marker-location' : ''} 
                                                                ${!item.isIncident && currentMapStyle === MAP_STYLES.SATELLITE ? 'map-marker--hybrid' : ''}`
                                                        }
                                                        style={{ zIndex: item.isIncident ? 2 : 1 }}
                                                    >
                                                        <img src={markerIcon} alt="location" />
                                                    </div>
                                                }
                                                options={{
                                                    position: [item.coordinates.longitude, item.coordinates.latitude],
                                                    text: `${item.location.name}: ${item.statistics.totalVictims} victims`,
                                                    popup: popups[item.location.id!]
                                                }}
                                                events={[
                                                    { eventName: 'click', callback: () => handleMarkerClick(item) },
                                                    { eventName: 'mouseover', callback: () => setHoveredLocationId(item.location.id!) },
                                                    { eventName: 'mouseout', callback: () => setHoveredLocationId(null) }
                                                ]}
                                            />
                                        );
                                    })}
                            </AzureMap>
                        </AzureMapsProvider>
                        {showLoading && (
                            <div className="loading-overlay">
                                <div className="loading-content">
                                    <Spinner />
                                    <Text>Loading locations...</Text>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="base-map__drawer">
                    {mode === 'viewer' ? (
                        <Drawer
                            className="locations-drawer"
                            type="inline"
                            open={true}
                            position="end"
                        >
                            <DrawerHeader>
                                Map Victims
                            </DrawerHeader>
                            <DrawerBody>
                                <div className="locations-list">
                                    <LoadingWrapper
                                        isLoading={!locationsData}
                                        isEmpty={locations.length === 0}
                                        emptyMessage="No Locations Found"
                                    >
                                        {locations.map(item => (
                                            <div key={item.location.id} className="location-item">
                                                <div className="location-item__section">
                                                    <div>
                                                        <span className="location-item__title">{item.location.name}</span>
                                                    </div>
                                                    <div>
                                                        <span className="location-item__label">Victims: </span>
                                                        <span className="location-item__value">{item.statistics.totalVictims}</span>
                                                    </div>
                                                    <div>
                                                        <span className="location-item__label">Matches: </span>
                                                        <span className="location-item__value">{item.statistics.totalMatched}</span>
                                                    </div>
                                                    <div>
                                                        <span className="location-item__label">Notifications Completed: </span>
                                                        <span className="location-item__value">{item.statistics.totalConnected}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </LoadingWrapper>
                                </div>
                            </DrawerBody>
                        </Drawer>
                    ) : mode === 'admin' && (
                        <Drawer
                            className="victims-drawer"
                            type="inline"
                            open={!!selectedLocation}
                            position="end"
                            onOpenChange={(_, { open }) => !open && setSelectedLocation(null)}
                        >
                            <DrawerHeader>
                                <DrawerHeaderTitle
                                    action={
                                        <Button
                                            appearance="subtle"
                                            aria-label="Close"
                                            icon={<Dismiss16Regular />}
                                            onClick={() => setSelectedLocation(null)}
                                        />
                                    }
                                >
                                    {selectedLocation ? `${selectedLocation.location.name}: ${selectedLocation.statistics.totalVictims} Victims` : ''}
                                </DrawerHeaderTitle>
                            </DrawerHeader>
                            <DrawerBody>
                                <div className="victims-list">
                                    <LoadingWrapper
                                        isLoading={!victimsData}
                                        isEmpty={!victimsData?.items?.length}
                                        emptyMessage="No Victims Found"
                                    >
                                        {victimsData?.items?.map(victim => (
                                            <div key={victim.id} className="victim-item">
                                                <div className="victim-item__section">
                                                    <div className="person-image">
                                                        <LazyImage
                                                            src={victim.pictureUrl}
                                                            alt={victim.fullName || 'Victim'}
                                                            fallback={<div className="person-image--no-image" />}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="victim-item__section">
                                                    <div>
                                                        <span className="victim-item__title">{victim.fullName}</span>
                                                    </div>
                                                    <div>
                                                        <span className="victim-item__label">Status: </span>
                                                        <span className="victim-item__value">{victim.status}</span>
                                                    </div>
                                                    <div>
                                                        <span className="victim-item__label">Contact: </span>
                                                        <span className="victim-item__value">
                                                            {victim?.matchedPerson?.publicContact ? (
                                                                `${victim.matchedPerson.publicContact.firstName} ${victim.matchedPerson.publicContact.lastName}`
                                                            ) : "-"}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <span className="victim-item__label">Restrictions: </span>
                                                        <span className="victim-item__value">{victim.restrictions ? "Yes" : "No"}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </LoadingWrapper>
                                </div>
                            </DrawerBody>
                        </Drawer>
                    )}
                </div>
            </div>
            {isPdfLoading && (
                <div className="loading-overlay">
                    <div className="loading-content">
                        <Spinner />
                        <Text>Generating PDF...</Text>
                        <ProgressBar value={pdfProgress.current} max={pdfProgress.total} />
                    </div>
                </div>
            )}
        </>
    );
};

export default BaseMapVictims;
